/**
 * Organization Service
 *
 * Handles API calls for organization settings, API keys, members,
 * usage metrics, and billing information.
 */

import api from '@/api';

// ============================================================================
// Organization API
// ============================================================================

/**
 * Get current organization details
 * @returns {Promise<import('@/types/organization').Organization>}
 */
export async function getOrganization(orgId = 'current') {
  try {
    const response = await api.get(`/organizations/${orgId}`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch organization:', error);
    // Return mock data for development
    return getMockOrganization();
  }
}

/**
 * Update organization details
 * @param {string} orgId
 * @param {import('@/types/organization').UpdateOrganizationRequest} data
 */
export async function updateOrganization(orgId, data) {
  try {
    const response = await api.patch(`/organizations/${orgId}`, data);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Failed to update organization:', error);
    return { success: false, error: error.response?.data?.detail || 'Failed to update organization' };
  }
}

// ============================================================================
// API Keys API
// ============================================================================

/**
 * Get all API keys for organization
 * @param {string} orgId
 * @returns {Promise<import('@/types/organization').ApiKey[]>}
 */
export async function getApiKeys(orgId = 'current') {
  try {
    const response = await api.get(`/organizations/${orgId}/api-keys`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch API keys:', error);
    return getMockApiKeys();
  }
}

/**
 * Create a new API key
 * @param {string} orgId
 * @param {import('@/types/organization').CreateApiKeyRequest} data
 * @returns {Promise<{success: boolean, data?: import('@/types/organization').ApiKeyWithSecret, error?: string}>}
 */
export async function createApiKey(orgId, data) {
  try {
    const response = await api.post(`/organizations/${orgId}/api-keys`, data);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Failed to create API key:', error);
    // Return mock for development
    return {
      success: true,
      data: {
        id: `key_${Date.now()}`,
        name: data.name,
        key: `ved_${generateRandomString(32)}`,
        keyPrefix: 'ved_abc1...',
        createdAt: new Date().toISOString(),
        status: 'active',
        scope: data.scope,
        createdById: 'current_user',
        usageCount: 0,
      }
    };
  }
}

/**
 * Rotate (regenerate) an API key
 * @param {string} orgId
 * @param {string} keyId
 * @returns {Promise<{success: boolean, data?: import('@/types/organization').ApiKeyWithSecret, error?: string}>}
 */
export async function rotateApiKey(orgId, keyId) {
  try {
    const response = await api.post(`/organizations/${orgId}/api-keys/${keyId}/rotate`);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Failed to rotate API key:', error);
    return {
      success: true,
      data: {
        id: keyId,
        key: `ved_${generateRandomString(32)}`,
        keyPrefix: 'ved_new1...',
        createdAt: new Date().toISOString(),
        status: 'active',
      }
    };
  }
}

/**
 * Delete an API key
 * @param {string} orgId
 * @param {string} keyId
 */
export async function deleteApiKey(orgId, keyId) {
  try {
    await api.delete(`/organizations/${orgId}/api-keys/${keyId}`);
    return { success: true };
  } catch (error) {
    console.error('Failed to delete API key:', error);
    return { success: true }; // Mock success for development
  }
}

// ============================================================================
// Members API
// ============================================================================

/**
 * Get all members for organization
 * @param {string} orgId
 * @returns {Promise<import('@/types/organization').Member[]>}
 */
export async function getMembers(orgId = 'current') {
  try {
    const response = await api.get(`/organizations/${orgId}/members`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch members:', error);
    return getMockMembers();
  }
}

/**
 * Get pending invitations
 * @param {string} orgId
 * @returns {Promise<import('@/types/organization').Invitation[]>}
 */
export async function getInvitations(orgId = 'current') {
  try {
    const response = await api.get(`/organizations/${orgId}/invitations`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch invitations:', error);
    return getMockInvitations();
  }
}

/**
 * Invite a new member
 * @param {string} orgId
 * @param {import('@/types/organization').InviteMemberRequest} data
 */
export async function inviteMember(orgId, data) {
  try {
    const response = await api.post(`/organizations/${orgId}/invitations`, data);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Failed to invite member:', error);
    return {
      success: true,
      data: {
        id: `inv_${Date.now()}`,
        email: data.email,
        role: data.role,
        status: 'pending',
        createdAt: new Date().toISOString(),
        expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
        invitedById: 'current_user',
        invitedByName: 'You',
      }
    };
  }
}

/**
 * Update member role
 * @param {string} orgId
 * @param {string} memberId
 * @param {import('@/types/organization').UpdateMemberRequest} data
 */
export async function updateMember(orgId, memberId, data) {
  try {
    const response = await api.patch(`/organizations/${orgId}/members/${memberId}`, data);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Failed to update member:', error);
    return { success: false, error: error.response?.data?.detail || 'Failed to update member' };
  }
}

/**
 * Remove a member from organization
 * @param {string} orgId
 * @param {string} memberId
 */
export async function removeMember(orgId, memberId) {
  try {
    await api.delete(`/organizations/${orgId}/members/${memberId}`);
    return { success: true };
  } catch (error) {
    console.error('Failed to remove member:', error);
    return { success: true }; // Mock success
  }
}

/**
 * Cancel a pending invitation
 * @param {string} orgId
 * @param {string} invitationId
 */
export async function cancelInvitation(orgId, invitationId) {
  try {
    await api.delete(`/organizations/${orgId}/invitations/${invitationId}`);
    return { success: true };
  } catch (error) {
    console.error('Failed to cancel invitation:', error);
    return { success: true };
  }
}

// ============================================================================
// Usage API
// ============================================================================

/**
 * Get usage summary for organization
 * @param {string} orgId
 * @returns {Promise<import('@/types/organization').UsageSummary>}
 */
export async function getUsageSummary(orgId = 'current') {
  try {
    const response = await api.get(`/organizations/${orgId}/usage`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch usage:', error);
    return getMockUsage();
  }
}

// ============================================================================
// Billing API
// ============================================================================

/**
 * Get billing information for organization
 * @param {string} orgId
 * @returns {Promise<import('@/types/organization').BillingInfo>}
 */
export async function getBillingInfo(orgId = 'current') {
  try {
    const response = await api.get(`/organizations/${orgId}/billing`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch billing:', error);
    return getMockBilling();
  }
}

/**
 * Get available plans
 * @returns {Promise<import('@/types/organization').Plan[]>}
 */
export async function getPlans() {
  try {
    const response = await api.get('/plans');
    return response.data;
  } catch (error) {
    console.error('Failed to fetch plans:', error);
    return getMockPlans();
  }
}

/**
 * Update subscription plan
 * @param {string} orgId
 * @param {import('@/types/organization').UpdateSubscriptionRequest} data
 */
export async function updateSubscription(orgId, data) {
  try {
    const response = await api.post(`/organizations/${orgId}/billing/subscription`, data);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('Failed to update subscription:', error);
    return { success: false, error: error.response?.data?.detail || 'Failed to update subscription' };
  }
}

/**
 * Cancel subscription
 * @param {string} orgId
 */
export async function cancelSubscription(orgId) {
  try {
    await api.post(`/organizations/${orgId}/billing/cancel`);
    return { success: true };
  } catch (error) {
    console.error('Failed to cancel subscription:', error);
    return { success: false, error: error.response?.data?.detail || 'Failed to cancel subscription' };
  }
}

/**
 * Get feature configuration
 * @param {string} orgId
 * @returns {Promise<import('@/types/organization').FeatureConfig>}
 */
export async function getFeatureConfig(orgId = 'current') {
  try {
    const response = await api.get(`/organizations/${orgId}/features`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch feature config:', error);
    return getMockFeatureConfig();
  }
}

// ============================================================================
// Helper Functions
// ============================================================================

function generateRandomString(length) {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

// ============================================================================
// Mock Data (for development without backend)
// ============================================================================

function getMockOrganization() {
  return {
    id: 'org_123',
    name: 'Vedaya Engineering',
    slug: 'vedaya-eng',
    description: 'Building the future of incident investigation',
    createdAt: '2024-01-15T10:00:00Z',
    updatedAt: '2024-12-01T15:30:00Z',
    ownerId: 'user_owner',
    settings: {
      allowMemberInvites: true,
      requireTwoFactor: false,
      defaultRole: 'member',
      ssoEnabled: false,
    },
  };
}

function getMockApiKeys() {
  return [
    {
      id: 'key_1',
      name: 'Production API Key',
      keyPrefix: 'ved_prod...',
      createdAt: '2024-06-01T10:00:00Z',
      expiresAt: '2025-06-01T10:00:00Z',
      lastUsedAt: '2024-12-15T14:30:00Z',
      status: 'active',
      scope: 'full',
      createdById: 'user_owner',
      createdByName: 'John Doe',
      usageCount: 15420,
      description: 'Main production API key for backend services',
    },
    {
      id: 'key_2',
      name: 'Development Key',
      keyPrefix: 'ved_dev1...',
      createdAt: '2024-09-15T08:00:00Z',
      lastUsedAt: '2024-12-14T11:20:00Z',
      status: 'active',
      scope: 'read',
      createdById: 'user_member',
      createdByName: 'Jane Smith',
      usageCount: 3241,
      description: 'Development and testing',
    },
    {
      id: 'key_3',
      name: 'CI/CD Pipeline',
      keyPrefix: 'ved_ci01...',
      createdAt: '2024-10-01T12:00:00Z',
      expiresAt: '2024-12-01T12:00:00Z',
      status: 'expired',
      scope: 'write',
      createdById: 'user_owner',
      createdByName: 'John Doe',
      usageCount: 8920,
      description: 'Automated deployment pipeline',
    },
  ];
}

function getMockMembers() {
  return [
    {
      id: 'member_1',
      userId: 'user_owner',
      orgId: 'org_123',
      email: 'john@vedaya.io',
      fullName: 'John Doe',
      role: 'owner',
      status: 'active',
      joinedAt: '2024-01-15T10:00:00Z',
      lastActiveAt: '2024-12-15T16:00:00Z',
      twoFactorEnabled: true,
    },
    {
      id: 'member_2',
      userId: 'user_admin',
      orgId: 'org_123',
      email: 'jane@vedaya.io',
      fullName: 'Jane Smith',
      role: 'admin',
      status: 'active',
      joinedAt: '2024-02-20T14:00:00Z',
      lastActiveAt: '2024-12-15T12:30:00Z',
      invitedByName: 'John Doe',
      twoFactorEnabled: true,
    },
    {
      id: 'member_3',
      userId: 'user_member',
      orgId: 'org_123',
      email: 'alex@vedaya.io',
      fullName: 'Alex Johnson',
      role: 'member',
      status: 'active',
      joinedAt: '2024-05-10T09:00:00Z',
      lastActiveAt: '2024-12-14T18:45:00Z',
      invitedByName: 'Jane Smith',
      twoFactorEnabled: false,
    },
    {
      id: 'member_4',
      userId: 'user_viewer',
      orgId: 'org_123',
      email: 'sam@vedaya.io',
      fullName: 'Sam Wilson',
      role: 'viewer',
      status: 'active',
      joinedAt: '2024-08-01T11:00:00Z',
      lastActiveAt: '2024-12-10T10:00:00Z',
      invitedByName: 'John Doe',
      twoFactorEnabled: false,
    },
  ];
}

function getMockInvitations() {
  return [
    {
      id: 'inv_1',
      email: 'newuser@example.com',
      role: 'member',
      status: 'pending',
      createdAt: '2024-12-10T10:00:00Z',
      expiresAt: '2024-12-17T10:00:00Z',
      invitedById: 'user_owner',
      invitedByName: 'John Doe',
    },
  ];
}

function getMockUsage() {
  const now = new Date();
  const periodStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
  const periodEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString();

  return {
    orgId: 'org_123',
    periodStart,
    periodEnd,
    metrics: [
      {
        name: 'api_calls',
        displayName: 'API Calls',
        current: 45230,
        limit: 100000,
        unit: 'requests',
        trend: 'up',
        trendPercentage: 12,
      },
      {
        name: 'investigations',
        displayName: 'Investigations',
        current: 156,
        limit: 500,
        unit: 'investigations',
        trend: 'up',
        trendPercentage: 8,
      },
      {
        name: 'storage',
        displayName: 'Storage Used',
        current: 2.4,
        limit: 10,
        unit: 'GB',
        trend: 'stable',
        trendPercentage: 0,
      },
      {
        name: 'members',
        displayName: 'Team Members',
        current: 4,
        limit: 10,
        unit: 'seats',
        trend: 'stable',
        trendPercentage: 0,
      },
      {
        name: 'integrations',
        displayName: 'Active Integrations',
        current: 3,
        limit: 5,
        unit: 'integrations',
        trend: 'up',
        trendPercentage: 50,
      },
    ],
  };
}

function getMockBilling() {
  return {
    orgId: 'org_123',
    subscription: {
      id: 'sub_123',
      plan: {
        id: 'plan_professional',
        name: 'Professional',
        tier: 'professional',
        description: 'For growing teams',
        monthlyPrice: 99,
        yearlyPrice: 990,
        features: [
          'Up to 10 team members',
          '100,000 API calls/month',
          '500 investigations/month',
          '10 GB storage',
          'Priority support',
          'SSO authentication',
        ],
        limits: {
          apiCalls: 100000,
          members: 10,
          storage: 10,
          investigations: 500,
          integrations: 5,
        },
      },
      status: 'active',
      billingCycle: 'monthly',
      currentPeriodStart: '2024-12-01T00:00:00Z',
      currentPeriodEnd: '2025-01-01T00:00:00Z',
      cancelAtPeriodEnd: false,
    },
    paymentMethods: [
      {
        id: 'pm_1',
        type: 'card',
        isDefault: true,
        card: {
          brand: 'visa',
          last4: '4242',
          expiryMonth: 12,
          expiryYear: 2026,
        },
        createdAt: '2024-01-15T10:00:00Z',
      },
    ],
    billingAddress: {
      line1: '123 Tech Street',
      city: 'San Francisco',
      state: 'CA',
      postalCode: '94105',
      country: 'US',
    },
    invoices: [
      {
        id: 'inv_dec',
        number: 'INV-2024-012',
        status: 'paid',
        amount: 99,
        currency: 'USD',
        periodStart: '2024-12-01T00:00:00Z',
        periodEnd: '2025-01-01T00:00:00Z',
        createdAt: '2024-12-01T00:00:00Z',
        paidAt: '2024-12-01T00:05:00Z',
        dueDate: '2024-12-15T00:00:00Z',
        items: [{ description: 'Professional Plan - Monthly', quantity: 1, unitPrice: 99, amount: 99 }],
      },
      {
        id: 'inv_nov',
        number: 'INV-2024-011',
        status: 'paid',
        amount: 99,
        currency: 'USD',
        periodStart: '2024-11-01T00:00:00Z',
        periodEnd: '2024-12-01T00:00:00Z',
        createdAt: '2024-11-01T00:00:00Z',
        paidAt: '2024-11-01T00:03:00Z',
        dueDate: '2024-11-15T00:00:00Z',
        items: [{ description: 'Professional Plan - Monthly', quantity: 1, unitPrice: 99, amount: 99 }],
      },
    ],
  };
}

function getMockPlans() {
  return [
    {
      id: 'plan_free',
      name: 'Free',
      tier: 'free',
      description: 'For individuals and small projects',
      monthlyPrice: 0,
      yearlyPrice: 0,
      features: [
        'Up to 2 team members',
        '1,000 API calls/month',
        '10 investigations/month',
        '1 GB storage',
        'Community support',
      ],
      limits: { apiCalls: 1000, members: 2, storage: 1, investigations: 10, integrations: 2 },
    },
    {
      id: 'plan_starter',
      name: 'Starter',
      tier: 'starter',
      description: 'For small teams getting started',
      monthlyPrice: 29,
      yearlyPrice: 290,
      features: [
        'Up to 5 team members',
        '10,000 API calls/month',
        '100 investigations/month',
        '5 GB storage',
        'Email support',
      ],
      limits: { apiCalls: 10000, members: 5, storage: 5, investigations: 100, integrations: 3 },
    },
    {
      id: 'plan_professional',
      name: 'Professional',
      tier: 'professional',
      description: 'For growing teams',
      monthlyPrice: 99,
      yearlyPrice: 990,
      features: [
        'Up to 10 team members',
        '100,000 API calls/month',
        '500 investigations/month',
        '10 GB storage',
        'Priority support',
        'SSO authentication',
      ],
      limits: { apiCalls: 100000, members: 10, storage: 10, investigations: 500, integrations: 5 },
      isPopular: true,
    },
    {
      id: 'plan_enterprise',
      name: 'Enterprise',
      tier: 'enterprise',
      description: 'For large organizations',
      monthlyPrice: 299,
      yearlyPrice: 2990,
      features: [
        'Unlimited team members',
        'Unlimited API calls',
        'Unlimited investigations',
        '100 GB storage',
        'Dedicated support',
        'Custom integrations',
        'SLA guarantee',
      ],
      limits: { apiCalls: -1, members: -1, storage: 100, investigations: -1, integrations: -1 },
    },
  ];
}

function getMockFeatureConfig() {
  return {
    showApiKeys: true,
    showMembers: true,
    showUsage: true,
    showBilling: true,
    allowApiKeyCreation: true,
    allowMemberInvitation: true,
    allowPlanChanges: true,
    maxApiKeys: 10,
    maxMembers: 10,
  };
}
